import { createTheme } from '@mui/material/styles';

// IMO Color Palette from website
const imoColors = {
  black: '#000000',
  teal: '#206884',
  cream: '#FEFDF6',
  skyblue: '#74C0E6',
  brown: '#935325',
  lavender: '#BDB6FD',
  lime: '#6CCC16',
  forest: '#3A8830',
  blue: '#2096F3',
};

// Custom theme matching the IMO website theme
const themeOptions = {
  palette: {
    mode: 'light',
    primary: {
      main: imoColors.teal, // IMO Teal
      light: imoColors.skyblue, // IMO Sky Blue
      dark: '#1a5568',
      contrastText: '#ffffff',
    },
    secondary: {
      main: imoColors.blue, // IMO Blue
      light: '#4da8f5',
      dark: '#1875c4',
      contrastText: '#ffffff',
    },
    error: {
      main: '#ef4444',
      light: '#f87171',
      dark: '#dc2626',
    },
    warning: {
      main: '#f59e0b',
      light: '#fbbf24',
      dark: '#d97706',
    },
    info: {
      main: imoColors.skyblue, // IMO Sky Blue
      light: '#9dd4f0',
      dark: '#5ba8d4',
    },
    success: {
      main: imoColors.lime, // IMO Lime
      light: '#8dd83a',
      dark: imoColors.forest, // IMO Forest
    },
    background: {
      default: imoColors.cream, // IMO Cream
      paper: '#ffffff',
    },
    text: {
      primary: '#333333',
      secondary: '#666666',
    },
    divider: '#e5e5e5',
  },
  typography: {
    fontFamily: [
      'Inter',
      '-apple-system',
      'BlinkMacSystemFont',
      '"Segoe UI"',
      'Roboto',
      '"Helvetica Neue"',
      'Arial',
      'sans-serif',
    ].join(','),
    h1: {
      fontSize: '2.125rem',
      fontWeight: 600,
      lineHeight: 1.2,
    },
    h2: {
      fontSize: '1.875rem',
      fontWeight: 600,
      lineHeight: 1.3,
    },
    h3: {
      fontSize: '1.5rem',
      fontWeight: 600,
      lineHeight: 1.4,
    },
    h4: {
      fontSize: '1.25rem',
      fontWeight: 600,
      lineHeight: 1.4,
    },
    h5: {
      fontSize: '1.125rem',
      fontWeight: 600,
      lineHeight: 1.5,
    },
    h6: {
      fontSize: '1rem',
      fontWeight: 600,
      lineHeight: 1.5,
    },
    body1: {
      fontSize: '1rem',
      lineHeight: 1.5,
    },
    body2: {
      fontSize: '0.875rem',
      lineHeight: 1.5,
    },
    button: {
      fontSize: '0.875rem',
      fontWeight: 500,
      textTransform: 'none',
    },
  },
  shape: {
    borderRadius: 4, // Match existing border radius
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 4, // Match existing border radius
          padding: '8px 16px',
          fontSize: '14px',
          fontWeight: 500,
          textTransform: 'none',
          '&:hover': {
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          },
        },
        contained: {
          boxShadow: '0px 3px 10px 0px rgba(125,125,125,0.2)', // Match existing shadow
          '&:hover': {
            boxShadow: '0 4px 8px rgba(0,0,0,0.15)',
          },
        },
        containedPrimary: {
          backgroundColor: '#54667d',
          '&:hover': {
            backgroundColor: '#4B5A6F',
          },
        },
        containedSecondary: {
          backgroundColor: '#6366f1', // Use vibrant secondary color
          '&:hover': {
            backgroundColor: '#4f46e5',
          },
        },
        containedSuccess: {
          backgroundColor: '#10b981', // Use vibrant success color
          color: '#ffffff',
          '&:hover': {
            backgroundColor: '#059669',
          },
        },
        containedWarning: {
          backgroundColor: '#f59e0b', // Use vibrant warning color
          color: '#ffffff',
          '&:hover': {
            backgroundColor: '#d97706',
          },
        },
        containedError: {
          backgroundColor: '#ef4444', // Use vibrant error color
          color: '#ffffff',
          '&:hover': {
            backgroundColor: '#dc2626',
          },
        },
        containedInfo: {
          backgroundColor: '#06b6d4', // Use vibrant info color
          color: '#ffffff',
          '&:hover': {
            backgroundColor: '#0891b2',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          boxShadow: '0px 2px 4px rgba(0,0,0,0.1)',
          borderRadius: 8,
          '&:hover': {
            boxShadow: '0px 4px 8px rgba(0,0,0,0.15)',
          },
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: imoColors.teal, // IMO Teal header
          boxShadow: '0px 3px 10px 0px rgba(125,125,125,0.2)',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        containedPrimary: {
          backgroundColor: imoColors.teal,
          '&:hover': {
            backgroundColor: '#1a5568',
          },
        },
        containedSecondary: {
          backgroundColor: imoColors.blue,
          '&:hover': {
            backgroundColor: '#1875c4',
          },
        },
        containedSuccess: {
          backgroundColor: imoColors.lime,
          '&:hover': {
            backgroundColor: imoColors.forest,
          },
        },
      },
    },
  },
  spacing: 8, // 8px base spacing unit
};

// Create the theme
const theme = createTheme(themeOptions);

export default theme;
